import { useCallback } from 'react';
import { getText } from 'i18n/intl';
import { ObjectGenerationDropDown } from 'pages/configuration/objectGeneration/components/ObjectGenerationDropDown';
import { fieldsSelector } from 'slices/field.slice';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import {
  EActivityDraftGroupConfigFieldOptions,
  type TActivityDraftGroupConfig,
  type TActivityDraftGroupConfigFieldOptionsValuesSchema,
} from '@timeedit/types/lib/types/activityGroup.type';
import { Icon, Tag } from '@timeedit/ui-components';
import { fieldOrNamingValueToString } from '../utils';

export const ArrayOfRules = ({
  currentTemplate,
  currentArrayOfRules,
  arrayOfRulesChanged,
}: {
  currentTemplate: TActivityDraftGroupConfig;
  currentArrayOfRules: TActivityDraftGroupConfigFieldOptionsValuesSchema[];
  arrayOfRulesChanged: (
    changedValue: TActivityDraftGroupConfigFieldOptionsValuesSchema[]
  ) => void;
}) => {
  const objectTypes = useAppSelector(selectObjectTypesList);
  const fields = useAppSelector(fieldsSelector);
  const onDeleteRule = (index: number) => {
    const newRules = currentArrayOfRules.filter((_, i) => i !== index);
    arrayOfRulesChanged(newRules);
  };
  const onAddRule = (
    typeAndValue: TActivityDraftGroupConfigFieldOptionsValuesSchema
  ) => {
    const newRules = [...(currentArrayOfRules || []), typeAndValue];
    arrayOfRulesChanged(newRules);
  };

  const onEditRule = (
    index: number,
    typeAndValue: TActivityDraftGroupConfigFieldOptionsValuesSchema
  ) => {
    const newRules = currentArrayOfRules.map((rule, i) => {
      if (i === index) {
        return typeAndValue;
      }
      return rule;
    });
    arrayOfRulesChanged(newRules);
  };

  const displayValue = useCallback(
    (value) => {
      return fieldOrNamingValueToString(value, fields, objectTypes);
    },
    [fields, objectTypes]
  );

  return (
    <div>
      <div
        className={'te-mt-2 te-border-[1px] te-rounded-md te-p-1 te-flex'}
        style={{ borderColor: 'rgba(0,0,0,0.45)' }}
      >
        {currentArrayOfRules &&
          currentArrayOfRules.length > 0 &&
          currentArrayOfRules.map((currentRule, i) => (
            <ObjectGenerationDropDown
              currentTemplate={currentTemplate}
              key={JSON.stringify(currentTemplate) + i}
              onSelect={(typeAndValue, rowIndex) => {
                const index = isNaN(rowIndex) ? i : rowIndex;
                onEditRule(index, typeAndValue);
              }}
              useNumbering
              value={currentRule}
              rowId={i}
            >
              <Tag
                className={'te-cursor-pointer'}
                color={
                  currentRule.type ===
                  EActivityDraftGroupConfigFieldOptions.STATIC_TEXT
                    ? 'default'
                    : 'success'
                }
              >
                {displayValue(currentRule)}
                <Icon
                  id="CloseOutlined"
                  alt="Close"
                  className={'te-ml-1'}
                  onClick={() => {
                    onDeleteRule(i);
                  }}
                />
              </Tag>
            </ObjectGenerationDropDown>
          ))}
        <ObjectGenerationDropDown
          currentTemplate={currentTemplate}
          onSelect={onAddRule}
          useNumbering
        >
          <Tag
            className={'te-cursor-pointer'}
            dashed={true}
            icon={<Icon id="PlusOutlined" alt="Plus" />}
          >
            Add Component{' '}
          </Tag>
        </ObjectGenerationDropDown>
      </div>
      <div className={'te-mt-2'}>
        <span className={'te-font-semibold'}>{getText('preview')}: </span>
        {currentArrayOfRules &&
          currentArrayOfRules.length > 0 &&
          currentArrayOfRules.map((currentRule, i) => (
            <span key={i}>{displayValue(currentRule)}</span>
          ))}
      </div>
    </div>
  );
};
